import React, { useState, useEffect, useRef } from 'react';
import {
    Button,
    Checkbox,
    DatePicker,
    Popconfirm,
    Radio,
    RadioChangeEvent,
    Tooltip,
    Typography,
} from 'antd';
import jaJP from 'antd/lib/date-picker/locale/ja_JP';
import ExportOutlined from '@ant-design/icons/lib/icons/ExportOutlined';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { RangeValue } from 'rc-picker/lib/interface';
import moment, { Moment } from 'moment';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { QuestionCircleOutlined } from '@ant-design/icons';

const RadioGroup = Radio.Group;
const { RangePicker } = DatePicker;

type Options = Partial<{
    startMonth: number
    endMonth: number
    isSimpleExport: boolean
}>;

type Props = {
    disabled: boolean
    disabledMessage: JSX.Element | string
    handleCancel?: () => void
    handleOk: (encoding: Encoding, options?: Options, isWithBelongs?: boolean) => void
    loading: boolean
    size?: SizeType
    term?: string
    title?: string
    isUserManagement?: boolean
    isShowWithBelongs?: boolean
    isAnonymous?: boolean
}

const DownloadCSVConfirm: React.FC<Props> = ({
    disabled,
    disabledMessage,
    handleCancel,
    handleOk,
    loading,
    size = 'middle',
    term = '',
    title = 'CSVエクスポート',
    isUserManagement = false,
    isShowWithBelongs = false,
    isAnonymous = false,
}) => {
    const popconfirmRef = useRef<{ forcePopupAlign?: () => void } | null>(null);
    const isYear = term.endsWith('年度') ? true : false;

    const [encoding, setEncoding] = useState<Encoding>('sjis');
    const [startMonth, setStartMonth] = useState<number>(moment().startOf('month').unix());
    const [endMonth, setEndMonth] = useState<number>(moment().endOf('month').unix());
    const [isSimpleExport, setIsSimpleExport] = useState(false);
    const [isWithBelongs, setIsWithBelongs] = useState(false);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const clientRect = buttonRef.current?.getBoundingClientRect();
    const x = clientRect?.left ?? 0;
    // PopconfirmTitle の maxWidth - 100px
    // -100px に明確な理由はない
    const isEnoughSpace = x > 820;

    const onConfirm = () => {
        if (typeof handleOk === 'function') {
            if (isYear) {
                handleOk(encoding, { startMonth, endMonth });
            }

            if (isSimpleExport) {
                handleOk(encoding, { isSimpleExport: true });
            }

            if (!isYear && !isSimpleExport) {
                handleOk(encoding, { startMonth: 0, endMonth: 0 }, isWithBelongs);
            }

        }
        //再度開くタイミングではRangePickerの入力値が初期化されているためStateも初期化する
        setStartMonth(moment().startOf('month').unix());
        setEndMonth(moment().endOf('month').unix());
    };

    const onCancel = handleCancel;

    const handleChangeEncodeValue = (e: RadioChangeEvent) => {
        setEncoding(e.target.value);
    };

    const handleChangeRenegePicker = (values: RangeValue<Moment>, dateStrings: [string, string]) => {
        if (!dateStrings) return;

        setStartMonth(moment(dateStrings[0]).startOf('month').unix());
        setEndMonth(moment(dateStrings[1]).endOf('month').unix());
    };

    const handleChangeSimpleMode = (e: RadioChangeEvent) => {
        setIsSimpleExport(e.target.checked);
    };

    const handleChangeWithBelongs = (e: CheckboxChangeEvent) => {
        setIsWithBelongs(e.target.checked);
    };

    const handleClose = (visible: boolean) => {
        if (visible) return;
        setIsSimpleExport(false);
        setIsWithBelongs(false);
        setStartMonth(moment().startOf('month').unix());
        setEndMonth(moment().endOf('month').unix());
    }

    useEffect(() => {
        // loading が false になったタイミングで popup の位置を再計算する
        if (loading) return;
        const rafId = requestAnimationFrame(() => {
                popconfirmRef.current?.forcePopupAlign?.();
            });

        // コンポーネントのアンマウントや loading の変化時に raf をキャンセルする
        return () => cancelAnimationFrame(rafId);
    }, [loading]);

    if (disabled) {
        return (
            <Tooltip title={disabledMessage}>
                <Button
                    disabled={disabled}
                    icon={<ExportOutlined />}
                    loading={loading}
                    size={size}
                    type='primary'
                >
                    {title}
                </Button>
            </Tooltip>
        );
    }

    return (
        <Popconfirm
            ref={popconfirmRef}
            autoAdjustOverflow
            cancelText='キャンセル'
            okText='CSVダウンロード'
            onCancel={onCancel}
            onConfirm={onConfirm}
            onVisibleChange={handleClose}
            placement={isEnoughSpace ? 'topRight' : 'top'}
            title={
                <PopconfirmTitle
                    checked={isSimpleExport}
                    checkedWithBelongs={isWithBelongs}
                    encoding={encoding}
                    isAnonymous={isAnonymous}
                    isUserManagement={isUserManagement}
                    isYear={isYear}
                    isShowWithBelongs={isShowWithBelongs}
                    onChangeEncoding={handleChangeEncodeValue}
                    onChangeRange={handleChangeRenegePicker}
                    onChangeSimpleMode={handleChangeSimpleMode}
                    onChangeWithBelongs={handleChangeWithBelongs}
                />
            }
        >
            <Button
                icon={<ExportOutlined />}
                size={size}
                type='primary'
                ref={buttonRef}
            >
                {title}
            </Button>
        </Popconfirm>
    )
};

export default DownloadCSVConfirm;


const rangePickerRangesForMonth: { [range: string]: [moment.Moment, moment.Moment] } = {
    '今月のみ': [
        moment().startOf('month'),
        moment().endOf('month'),
    ],
    '先月のみ': [
        moment().subtract(1, 'months').startOf('month'),
        moment().subtract(1, 'months').endOf('month'),
    ],
    '過去２ヶ月': [
        moment().subtract(2, 'months').startOf('month'),
        moment().subtract(1, 'months').endOf('month'),
    ],
};

type PopconfirmProps = {
    checked: boolean,
    checkedWithBelongs: boolean,
    encoding: Encoding,
    isAnonymous?: boolean,
    isUserManagement?: boolean,
    isShowWithBelongs?: boolean,
    isYear: boolean,
    onChangeEncoding: (e: RadioChangeEvent) => void,
    onChangeRange: (values: RangeValue<Moment>, dateStrings: [string, string]) => void,
    onChangeSimpleMode?: (e: CheckboxChangeEvent) => void,
    onChangeWithBelongs?: (e: CheckboxChangeEvent) => void,
};

const PopconfirmTitle: React.FC<PopconfirmProps> = ({
    checked,
    checkedWithBelongs,
    encoding,
    isAnonymous,
    isUserManagement,
    isShowWithBelongs,
    isYear,
    onChangeEncoding,
    onChangeRange,
    onChangeSimpleMode,
    onChangeWithBelongs,
}) => {
    return (
        <div style={{maxWidth: '920px', width: '80vw'}}>
            <p>
                CSVをファイルダウンロードします。<br />
                対象件数により、CSVのダウンロードには時間がかかることがあります。
                ダウンロードの進捗確認とダウンロードは画面左下をご利用下さい。<br />
                同一ブラウザであれば、ブラウザを閉じても前回の結果をダウンロードすることが可能です。<br />
            </p>
            <p>
                CSVファイルの文字エンコードを指定してください。Excelで表示・編集するには「Shift-JIS形式」を選択してください。<br />
                Shift-JIS形式でダウンロードした場合は、UTF-8形式で表現される一部の文字がうまく表示されないことがあります。
            </p>
            <p>
                数字のゼロやハイフンなどの記号の欠落にご注意ください。
            </p>

            <div style={{ margin: '1.5em 0px' }}>
                <RadioGroup defaultValue={encoding} onChange={onChangeEncoding}>
                    <Radio value='sjis'>ShiftJIS形式</Radio>
                    <Radio value='utf8'>UTF-8形式</Radio>
                </RadioGroup>
            </div>
            {isShowWithBelongs && (
                <div style={{ margin: '1.5em 0' }}>
                <Checkbox
                    defaultChecked={checkedWithBelongs}
                    onChange={onChangeWithBelongs}
                    disabled={isAnonymous}
                >
                    所属情報を含める
                </Checkbox>
                {
                    isAnonymous && (
                        <Tooltip title='無記名式アンケートです。'>
                            <QuestionCircleOutlined />
                        </Tooltip>
                    )
                }
                <div>
                    <Typography.Text>
                        CSVには、回答時点の所属情報ではなく、CSV出力時点における所属情報が出力されます。
                    </Typography.Text>
                </div>
            </div>
            )}

            {isUserManagement && onChangeSimpleMode && (
                <Checkbox
                    defaultChecked={checked}
                    onChange={onChangeSimpleMode}
                >
                    UUID を出力する(この機能を用いて出力したファイルは、インポートにはご利用出来ません)
                </Checkbox>
            )}

            {isYear &&
                <div style={{ margin: '1.5em 0px' }}>
                    <p>
                        出力する期間を選択してください。期間を長くした場合、エクスポートされるまでに数時間かかる場合があります。
                    </p>
                    <RangePicker
                        locale={jaJP}
                        defaultValue={[moment(), moment()]}
                        defaultPickerValue={[moment().subtract(1, 'y'), moment().subtract(1, 'y')]}
                        format={'YYYY/MM'}
                        picker={'month'}
                        placeholder={['開始月', '終了月']}
                        onChange={onChangeRange}
                        ranges={rangePickerRangesForMonth}
                    />
                </div>
            }
        </div>
    );
}